// useOrthancAuth.ts
// Shared Orthanc authentication layer used by both orthanc-api.ts (proprietary)
// and dicom-web-api.ts (DICOMweb). Provides a single JWT cache so tokens are
// not fetched redundantly when both clients target the same Orthanc instance.

import type { DicomArchiveConfig } from '@/src/supabase';

import { ref, watch } from 'vue';

import { session, supabase } from '@/src/supabase';

// Single shared JWT cache — keyed by OrthancConfig.id
const orthancJwts = ref<Record<string, string>>({});

// Fetch or return cached JWT for an Orthanc instance.
// Called by both OrthancAPIClient and DICOMwebClient.
export async function orthancJwt(id: string): Promise<string | null> {
  if (!session.value) return null;
  if (orthancJwts.value[id]) return orthancJwts.value[id];

  const { data: jwt, error } = await supabase.rpc('make_orthanc_jwt', { id });
  if (error) {
    console.error(error.message);
    return null;
  }
  if (!jwt) {
    console.error('No JWT returned for Orthanc ID:', id);
    return null;
  }
  orthancJwts.value[id] = jwt;
  return jwt;
}

// Clear all cached JWTs when the Supabase session changes (login/logout/refresh)
watch(session, () => {
  orthancJwts.value = {};
});

// Build the standard auth headers used by all Orthanc fetch calls.
// Both the proprietary API and DICOMweb use the same scheme.
export async function orthancHeaders(
  orthancConfig: DicomArchiveConfig,
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const token = await orthancJwt(orthancConfig.id);
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    ...(token ? { token } : {}),
    ...extra,
  };
}

// Base URL for the Orthanc proprietary REST API
// The /.. is because orthancConfig.url points to the dicom-web root
export function orthancBaseUrl(orthancConfig: DicomArchiveConfig): string {
  return `${orthancConfig.url}/..`;
}

// Base URL for the DICOMweb plugin API
export function dicomWebBaseUrl(orthancConfig: DicomArchiveConfig): string {
  return orthancConfig.url;
}
